import type { InventoryItem, MarketplaceListing } from "./types";
import type { CatalogueChange, CatalogueUpdateInput } from "./catalogue";
import { MAX_CATALOGUE_UPDATE } from "./catalogue";
import { parseCatalogueCost, purchaseCostIsKnown, validateAcquiredDate } from "./inventoryFacts";

export const SPREADSHEET_LIMITS = { bytes: 5_000_000, rows: 5000, columns: 80, cell: 32_000 } as const;
export const spreadsheetFields = [
  ["ignore", "Do not use"], ["item_id", "Listwork item ID"], ["sku", "SKU / stock code"],
  ["listing_id", "Marketplace listing ID or URL"], ["item_title", "Item title"],
  ["purchase_cost", "Purchase cost"], ["stock_location", "Stock location"],
  ["acquired_date", "Acquisition date"], ["currency", "Currency"], ["units", "Units in the cost total"],
] as const;
export type SpreadsheetField = typeof spreadsheetFields[number][0];
export type DetailField = "purchase_cost" | "stock_location" | "acquired_date";
export interface SpreadsheetSheet {
  name: string; rows: string[][];
  /** Zero-based row:column addresses whose Excel display cannot safely identify a value. */
  cellWarnings?: Record<string, SpreadsheetField[]>;
  /** Formula cells use saved results only; no workbook code is executed. */
  formulaCells?: string[];
}
export interface SpreadsheetOptions {
  dateOrder: "auto" | "dmy" | "mdy";
  numberStyle: "auto" | "dot" | "comma";
  costBasis: "" | "unit" | "lot";
  replaceExisting: boolean;
}
export const defaultSpreadsheetOptions: SpreadsheetOptions = { dateOrder: "auto", numberStyle: "auto", costBasis: "", replaceExisting: false };
export interface SpreadsheetEdit {
  itemID?: string;
  currency?: string;
  values?: Partial<Record<DetailField, string>>;
  clear?: DetailField[];
  deferred?: boolean;
}
export interface SpreadsheetIssue { field: SpreadsheetField; message: string; kind?: "duplicate" }
export interface SpreadsheetRow {
  key: string; rowNumber: number; original: Partial<Record<SpreadsheetField, string>>;
  values: Record<DetailField, string>; item?: InventoryItem; suggestions: InventoryItem[];
  issues: SpreadsheetIssue[]; change?: CatalogueChange;
  status: "ready" | "attention" | "unchanged" | "deferred" | "saved";
  match: "identifier" | "confirmed" | "none";
  costPerUnit?: string;
  keptFields?: DetailField[];
}
const aliases: Record<Exclude<SpreadsheetField, "ignore">, string[]> = {
  item_id: ["item_id", "listwork id", "listwork item id", "inventory id"],
  sku: ["sku", "stock code", "stock number", "product code", "custom label", "custom label sku", "seller sku", "reference", "ref"],
  listing_id: ["listing id", "listing number", "ebay item number", "ebay item id", "item number", "ebay id", "etsy listing id", "listing url", "ebay url", "listing link"],
  item_title: ["item_title", "title", "item", "item name", "product", "product name", "description", "listing title"],
  purchase_cost: ["purchase_cost", "cost", "cost price", "purchase price", "purchase cost", "paid", "price paid", "bought for", "unit cost", "cost per unit", "cost each", "lot cost", "lot total", "total cost"],
  stock_location: ["stock_location", "location", "storage", "storage box", "storage location", "box", "shelf", "bin", "bin location"],
  acquired_date: ["acquired_date", "acquired", "acquisition date", "purchase date", "date purchased", "date bought", "bought on", "date acquired"],
  currency: ["currency", "currency code", "cost currency"],
  units: ["units", "quantity", "qty", "lot quantity", "units in lot", "quantity purchased"],
};
const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[_\-()]/g, " ").replace(/\b(gbp|usd|eur)\b|[£$€]/g, "").replace(/\s+/g, " ").trim();
const aliasIndex = new Map(Object.entries(aliases).flatMap(([field, names]) => names.map((name) => [normalizeHeader(name), field as SpreadsheetField] as const)));
export function suggestSpreadsheetMapping(headers: string[]): SpreadsheetField[] {
  const used = new Set<SpreadsheetField>();
  return headers.map((header) => {
    const field = aliasIndex.get(normalizeHeader(header)) ?? "ignore";
    if (field !== "ignore" && used.has(field)) return "ignore";
    used.add(field); return field;
  });
}
export function suggestHeaderRow(rows: string[][]): number {
  let best = 0, score = 0;
  rows.slice(0, 20).forEach((row, index) => {
    const fields = suggestSpreadsheetMapping(row).filter((field) => field !== "ignore");
    if (fields.length > score) { best = index; score = fields.length; }
  });
  return best;
}
export function mappingKey(headers: string[]): string { return JSON.stringify(headers.map(normalizeHeader)); }
export function suggestCostBasis(headers: string[], mapping: SpreadsheetField[]): SpreadsheetOptions["costBasis"] {
  const label = headers[mapping.indexOf("purchase_cost")] ?? "";
  return /^(purchase_cost)$|\b(unit|each)\b/i.test(label) ? "unit" : "";
}
export function mappingIssues(mapping: SpreadsheetField[]): string[] {
  const mapped = mapping.filter((field) => field !== "ignore");
  const issues = new Set(mapped).size !== mapped.length ? ["Use each Listwork field only once."] : [];
  if (!mapped.some((field) => ["item_id", "sku", "listing_id", "item_title"].includes(field))) issues.push("Choose a column that identifies your items, such as SKU or item title.");
  if (!mapped.some((field) => ["purchase_cost", "stock_location", "acquired_date"].includes(field))) issues.push("Choose at least one detail to complete: cost, location or acquisition date.");
  return issues;
}

export function spreadsheetMappingErrors(mapping: SpreadsheetField[], options: SpreadsheetOptions): { columns: Record<number, string>; costBasis?: string } {
  const columns: Record<number, string> = {};
  mapping.forEach((field, index) => {
    if (field !== "ignore" && mapping.indexOf(field) !== index) columns[index] = "Use each Listwork field only once.";
  });
  if (!mapping.some((field) => ["item_id", "sku", "listing_id", "item_title"].includes(field))) columns[0] = "Choose a column that identifies your items, such as SKU or item title.";
  if (!mapping.some((field) => ["purchase_cost", "stock_location", "acquired_date"].includes(field))) columns[mapping.findIndex((field) => field === "ignore") < 0 ? 0 : mapping.indexOf("ignore")] = "Choose a detail to complete: cost, location or acquisition date.";
  if (mapping.includes("purchase_cost") && options.costBasis === "lot" && !mapping.includes("units")) columns[Math.max(0, mapping.indexOf("ignore"))] = "Match the column containing the number of units in each cost total.";
  return { columns, costBasis: mapping.includes("purchase_cost") && !options.costBasis ? "Choose whether your costs are per unit or for a whole lot." : undefined };
}

/** Parses cell evidence only. Semantic interpretation happens after column review. */
export function parseSpreadsheetText(input: string, delimiter?: string): string[][] {
  if (input.length > SPREADSHEET_LIMITS.bytes) throw new Error("Choose a spreadsheet under 5 MB.");
  const text = input.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n");
  if (!text.trim()) throw new Error("Paste some spreadsheet rows or choose a file first.");
  if (!delimiter) {
    // A comma in a location is evidence of cell content, not of more columns.
    // Rank complete parses by header recognition and consistent row widths.
    const candidates = ["\t", ",", ";"].flatMap((candidate) => {
      try {
        const rows = parseSpreadsheetText(text, candidate);
        const header = suggestHeaderRow(rows), width = rows[header]?.length ?? 0;
        const data = rows.slice(header).filter((row) => row.some((cell) => cell.trim()));
        const consistent = data.filter((row) => row.length === width).length / Math.max(1, data.length);
        const recognised = suggestSpreadsheetMapping(rows[header] ?? []).filter((field) => field !== "ignore").length;
        return [{ candidate, rows, score: width > 1 ? recognised * 10 + consistent * 5 : -1 }];
      } catch { return []; }
    }).sort((a, b) => b.score - a.score);
    if (candidates[0]?.score >= 0) return candidates[0].rows;
    // Re-run the likely source delimiter to retain a useful syntax/limit error.
    delimiter = text.split(/[\r\n]/, 1)[0].includes("\t") ? "\t" : text.split(/[\r\n]/, 1)[0].includes(";") ? ";" : ",";
  }
  const rows: string[][] = []; let row: string[] = [], cell = "", quoted = false, closed = false;
  const endCell = () => {
    if (cell.length > SPREADSHEET_LIMITS.cell) throw new Error("A cell is too long. Keep each cell under 32,000 characters.");
    row.push(cell); cell = ""; closed = false;
    if (row.length > SPREADSHEET_LIMITS.columns) throw new Error("Choose up to 80 columns from your spreadsheet.");
  };
  const endRow = () => { endCell(); rows.push(row); row = []; if (rows.length > SPREADSHEET_LIMITS.rows + 20) throw new Error("Use up to 5,000 item rows in one spreadsheet."); };
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (char === '"') { quoted = false; closed = true; }
      else cell += char;
    } else if (char === delimiter) endCell();
    else if (char === "\n" || char === "\r") endRow();
    else if (char === '"' && cell === "" && !closed) quoted = true;
    else if (closed && /\s/.test(char)) continue;
    else if (char === '"' || closed) throw new Error(`Check the quotation marks near row ${rows.length + 1}, or paste the cells again.`);
    else cell += char;
  }
  if (quoted) throw new Error("A quoted cell is unfinished. Paste or upload the complete selection.");
  endRow();
  while (rows.length && rows[rows.length - 1].every((value) => !value.trim())) rows.pop();
  return rows;
}
export function checkSpreadsheetSheet(sheet: SpreadsheetSheet, headerRow: number): void {
  if (!Number.isInteger(headerRow) || headerRow < 0 || headerRow >= sheet.rows.length) throw new Error("Choose the row containing your column headings.");
  const rows = sheet.rows.slice(headerRow + 1).filter((row) => row.some((cell) => cell.trim()));
  if (!rows.length) throw new Error("This worksheet has headings but no item rows. Choose another worksheet or header row.");
  if (rows.length > SPREADSHEET_LIMITS.rows) throw new Error("Use up to 5,000 item rows in one spreadsheet.");
  if (sheet.rows.some((row) => row.length > SPREADSHEET_LIMITS.columns || row.some((cell) => cell.length > SPREADSHEET_LIMITS.cell))) throw new Error("Use up to 80 columns, with cells under 32,000 characters.");
}

export function parseSpreadsheetCost(value: string, currency: string, style: SpreadsheetOptions["numberStyle"]): number {
  let text = value.trim();
  const code = text.match(/\b[A-Za-z]{3}\b/);
  if (code && code[0].toUpperCase() !== currency.toUpperCase()) throw new Error(`Use this item’s ${currency} currency.`);
  for (const [symbol, allowed] of [["£", ["GBP"]], ["€", ["EUR"]], ["$", ["USD", "CAD", "AUD", "NZD", "SGD", "HKD"]]] as const) {
    if (text.includes(symbol) && !(allowed as readonly string[]).includes(currency.toUpperCase())) throw new Error(`Use this item’s ${currency} currency.`);
  }
  text = text.replace(/\b[A-Za-z]{3}\b|[£€$]/g, "").trim();
  if (!/^[\d.,\s]+$/.test(text)) throw new Error("Enter a cost of 0 or more, such as 4.50.");
  if (/\s/.test(text)) {
    // Spaces can group thousands, including the narrow spaces used by Excel.
    // Never join arbitrary digits ("4 50" must not become 450).
    if (!/^\d{1,3}(?:[ \u00a0\u202f\u2009]\d{3})+(?:[.,]\d{1,2})?$/.test(text)) throw new Error("Check this cost’s decimal and thousands separators.");
    text = text.replace(/[ \u00a0\u202f\u2009]/g, "");
  }
  if (!/^[\d.,]+$/.test(text)) throw new Error("Enter a cost of 0 or more, such as 4.50.");
  let format = style;
  if (format === "auto") {
    if (text.includes(",") && text.includes(".")) format = text.lastIndexOf(".") > text.lastIndexOf(",") ? "dot" : "comma";
    else if (/^\d+[,.]\d{1,2}$/.test(text)) format = text.includes(",") ? "comma" : "dot";
    else if (/[,.]/.test(text)) throw new Error("Choose the number format to interpret this cost.");
    else format = "dot";
  }
  const pattern = format === "dot" ? /^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/ : /^(?:\d+|\d{1,3}(?:\.\d{3})+)(?:,\d{1,2})?$/;
  if (!pattern.test(text)) throw new Error("Check this cost’s decimal and thousands separators.");
  return parseCatalogueCost(format === "dot" ? text.replace(/,/g, "") : text.replace(/\./g, "").replace(",", "."));
}
export function inferDateOrder(values: string[]): SpreadsheetOptions["dateOrder"] {
  let dmy = false, mdy = false;
  for (const value of values) {
    const match = value.trim().match(/^(\d{1,2})[/.\-](\d{1,2})[/.\-]\d{4}$/);
    if (match) { if (+match[1] > 12) dmy = true; if (+match[2] > 12) mdy = true; }
  }
  return dmy !== mdy ? dmy ? "dmy" : "mdy" : "auto";
}
export function parseSpreadsheetDate(value: string, order: SpreadsheetOptions["dateOrder"], now = new Date()): string {
  let text = value.trim();
  const named = text.match(/^(\d{1,2})\s+([a-z]+)\s+(\d{4})$/i) ?? text.match(/^([a-z]+)\s+(\d{1,2}),?\s+(\d{4})$/i)?.map((part, index, all) => index === 1 ? all[2] : index === 2 ? all[1] : part);
  if (named) {
    const months = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
    const month = months.findIndex((name) => name === named[2].toLowerCase() || name.slice(0, 3) === named[2].toLowerCase());
    if (month >= 0) text = `${named[3]}-${String(month + 1).padStart(2, "0")}-${named[1].padStart(2, "0")}`;
  }
  const match = text.match(/^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})$/);
  if (match) {
    let resolved = order;
    if (resolved === "auto") {
      if (+match[1] > 12 && +match[2] <= 12) resolved = "dmy";
      else if (+match[2] > 12 && +match[1] <= 12) resolved = "mdy";
      else if (match[1].padStart(2, "0") === match[2].padStart(2, "0")) resolved = "dmy";
      else throw new Error("Choose day/month/year or month/day/year.");
    }
    text = `${match[3]}-${(resolved === "dmy" ? match[2] : match[1]).padStart(2, "0")}-${(resolved === "dmy" ? match[1] : match[2]).padStart(2, "0")}`;
  }
  validateAcquiredDate(text, now); return text;
}
const identity = (value: string) => value.trim().replace(/^'(?=[=+\-@])/, "").toLowerCase();
function listingIdentity(value: string) {
  const text = value.trim();
  try { const url = new URL(text); return url.pathname.match(/\/(?:itm|listing)\/(?:[^/]+\/)?(\d+)/)?.[1] ?? text; } catch { return text; }
}
const titleTokens = (value: string) => new Set(value.toLowerCase().normalize("NFKD").replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter((token) => token.length > 1));
export function suggestInventoryMatches(title: string, items: InventoryItem[]): InventoryItem[] {
  return inventoryMatcher(items)(title);
}
function inventoryMatcher(items: InventoryItem[]) {
  const indexed = items.map((item) => ({ item, tokens: titleTokens(item.title) }));
  const byTitle = new Map<string, InventoryItem[]>(), byToken = new Map<string, number[]>();
  indexed.forEach(({ item, tokens }, index) => {
    const title = item.title.trim().toLowerCase(); byTitle.set(title, [...(byTitle.get(title) ?? []), item]);
    tokens.forEach((token) => { const matches = byToken.get(token); if (matches) matches.push(index); else byToken.set(token, [index]); });
  });
  const cache = new Map<string, InventoryItem[]>();
  return (title: string) => {
    const key = title.trim().toLowerCase();
    if (byTitle.has(key)) return byTitle.get(key)!.slice(0, 5);
    if (cache.has(key)) return cache.get(key)!;
    const tokens = titleTokens(title);
    // Prioritise distinctive words. Generic words must not trigger a quadratic scan
    // of every inventory title for every row in a large sheet.
    const postings = [...tokens].map((token) => byToken.get(token) ?? []).filter((list) => list.length).sort((a, b) => a.length - b.length);
    const candidates = [...new Set(postings.slice(0, 2).flat())].slice(0, 500);
    const result = candidates.map((index) => {
      const { item, tokens: other } = indexed[index]; const shared = [...tokens].filter((token) => other.has(token)).length;
      return { item, score: shared / (tokens.size + other.size - shared) };
    }).filter(({ score }) => score >= 0.22).sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title)).slice(0, 5).map(({ item }) => item);
    cache.set(key, result); return result;
  };
}

export function analyseSpreadsheet(input: {
  sheet: SpreadsheetSheet; headerRow: number; mapping: SpreadsheetField[]; options: SpreadsheetOptions;
  edits: Record<string, SpreadsheetEdit>; items: InventoryItem[]; listings: MarketplaceListing[]; savedRows?: string[]; now?: Date;
}): SpreadsheetRow[] {
  const { sheet, headerRow, mapping, options, edits, listings } = input;
  const items = input.items.filter((item) => !["sold", "archived"].includes(item.status));
  const byID = new Map(items.map((item) => [item.id, item]));
  const suggestMatches = inventoryMatcher(items);
  const bySKU = new Map<string, Set<string>>(), byListing = new Map<string, Set<string>>();
  const add = (map: Map<string, Set<string>>, key: string, id: string) => { if (key) map.set(key, new Set([...(map.get(key) ?? []), id])); };
  items.forEach((item) => add(bySKU, identity(item.sku), item.id));
  listings.forEach((listing) => { if (byID.has(listing.itemID)) { if (listing.externalListingID) add(byListing, listingIdentity(listing.externalListingID), listing.itemID); if (listing.channelSKU) add(bySKU, identity(listing.channelSKU), listing.itemID); } });
  const dateColumn = mapping.indexOf("acquired_date");
  const dateOrder = options.dateOrder === "auto" ? inferDateOrder(sheet.rows.slice(headerRow + 1).map((row) => row[dateColumn] ?? "")) : options.dateOrder;
  const saved = new Set(input.savedRows ?? []);
  const rows: SpreadsheetRow[] = sheet.rows.slice(headerRow + 1).flatMap((cells, index) => {
    if (cells.every((cell) => !cell.trim())) return [];
    const rowNumber = headerRow + index + 2, key = String(rowNumber), edit = edits[key] ?? {};
    const formatNeedsReview = (field: SpreadsheetField) => sheet.cellWarnings?.[`${rowNumber - 1}:${mapping.indexOf(field)}`]?.includes(field);
    const original: SpreadsheetRow["original"] = {};
    mapping.forEach((field, column) => { if (field !== "ignore") original[field] = cells[column]?.trim() ?? ""; });
    const supplied = (field: DetailField) => /^(unknown|not known|n\/a|na|\?|[-–—])$/i.test(original[field] ?? "") ? "" : original[field] ?? "";
    const values: SpreadsheetRow["values"] = { purchase_cost: supplied("purchase_cost"), stock_location: supplied("stock_location"), acquired_date: supplied("acquired_date"), ...edit.values };
    const row: SpreadsheetRow = { key, rowNumber, original, values, issues: [], suggestions: [], status: "unchanged", match: "none" };
    if (saved.has(key)) { row.status = "saved"; return [row]; }
    const issue = (field: SpreadsheetField, message: string) => row.issues.push({ field, message });
    if (cells.length > mapping.length && cells.slice(mapping.length).some((cell) => cell.trim())) issue("ignore", "This row has more cells than the header. Check the header row or source file.");
    if (edit.itemID) { row.item = byID.get(edit.itemID); row.match = "confirmed"; if (!row.item) issue("item_id", "The chosen item is no longer available. Choose another match."); }
    else if (["item_id", "sku", "listing_id"].some((field) => formatNeedsReview(field as SpreadsheetField))) {
      issue("item_id", "This identifier uses Excel formatting. Choose the inventory item to confirm the match.");
    } else {
      const identities: Set<string>[] = [];
      if (original.item_id) identities.push(new Set(byID.has(original.item_id) ? [original.item_id] : []));
      if (original.sku) identities.push(bySKU.get(identity(original.sku)) ?? new Set());
      if (original.listing_id) identities.push(byListing.get(listingIdentity(original.listing_id)) ?? new Set());
      if (identities.length) {
        const intersection = [...identities[0]].filter((id) => identities.every((set) => set.has(id)));
        if (intersection.length === 1) { row.item = byID.get(intersection[0]); row.match = "identifier"; }
        else issue("item_id", identities.some((set) => !set.size) ? "No matching inventory item. Choose an item or leave this row for later." : intersection.length > 1 ? "More than one item has this identifier. Choose the right item." : "These identifiers point to different items. Choose the right item.");
      } else issue("item_id", original.item_title ? "Confirm an inventory match for this title." : "Choose the inventory item this row belongs to.");
    }
    if (!row.item && original.item_title) row.suggestions = suggestMatches(original.item_title);
    if (row.item) {
      const item = row.item, update: CatalogueChange["update"] = {};
      const present = (field: DetailField) => values[field].trim() !== "" || edit.clear?.includes(field);
      const canWrite = (field: DetailField) => options.replaceExisting || (field === "purchase_cost" ? !purchaseCostIsKnown(item) : field === "stock_location" ? !item.stockLocation : !item.acquiredAt);
      const currency = edit.currency ?? original.currency;
      if (currency && currency.toUpperCase() !== item.purchaseCost.currency.toUpperCase() && present("purchase_cost") && canWrite("purchase_cost")) issue("currency", `This item uses ${item.purchaseCost.currency}; check the currency column.`);
      if (formatNeedsReview("currency") && edit.currency === undefined && present("purchase_cost") && canWrite("purchase_cost")) issue("currency", "Confirm this item’s currency; the spreadsheet uses a custom Excel format.");
      for (const field of ["purchase_cost", "stock_location", "acquired_date"] as const) {
        if (!present(field)) continue;
        try {
          if (edit.clear?.includes(field)) {
            if (!canWrite(field)) throw new Error("Choose ‘Replace existing details’ above to clear a saved value.");
            if (field === "purchase_cost" && purchaseCostIsKnown(item)) Object.assign(update, { purchaseCostMinor: 0, purchaseCostStatus: "unknown" });
            if (field === "stock_location" && item.stockLocation) update.stockLocation = null;
            if (field === "acquired_date" && item.acquiredAt) update.acquiredAt = null;
            continue;
          }
          if (!canWrite(field)) { (row.keptFields ??= []).push(field); continue; }
          if (edit.values?.[field] === undefined && formatNeedsReview(field)) throw new Error("This cell uses a custom Excel format. Enter the value shown in your spreadsheet.");
          if (field === "purchase_cost") {
            // In-app corrections are explicitly labelled per unit.
            const basis = edit.values?.purchase_cost !== undefined ? "unit" : options.costBasis;
            if (!basis) throw new Error("Choose whether these costs are per unit or for a whole lot.");
            let amount = parseSpreadsheetCost(values[field], item.purchaseCost.currency, edit.values?.purchase_cost !== undefined ? "auto" : options.numberStyle);
            if (basis === "lot") {
              if (formatNeedsReview("units")) throw new Error("The unit count uses a custom Excel format. Enter the cost per unit instead.");
              const units = original.units ?? "";
              if (!/^\d+$/.test(units) || +units < 1 || !Number.isSafeInteger(+units)) throw new Error("A lot cost needs the number of units purchased. Match a units column in column setup.");
              if (amount % +units !== 0) throw new Error("This total cannot be divided into exact pennies. Enter the cost per unit.");
              amount /= +units;
            }
            row.costPerUnit = (amount / 100).toFixed(2);
            if (canWrite(field) && (!purchaseCostIsKnown(item) || amount !== item.purchaseCost.amountMinor)) Object.assign(update, { purchaseCostMinor: amount, purchaseCostStatus: "known" });
          } else if (field === "stock_location") {
            const location = values[field].trim();
            if (location.length > 120) throw new Error("Keep locations under 120 characters.");
            if (canWrite(field) && location !== (item.stockLocation ?? "")) update.stockLocation = location;
          } else {
            const date = parseSpreadsheetDate(values[field], dateOrder, input.now);
            if (canWrite(field) && date !== item.acquiredAt?.slice(0, 10)) update.acquiredAt = date;
          }
        } catch (error) { issue(field, error instanceof Error ? error.message : "Check this value."); }
      }
      if (Object.keys(update).length) row.change = { itemID: item.id, expectedUpdatedAt: item.updatedAt, update };
    }
    row.status = edit.deferred ? "deferred" : row.issues.length ? "attention" : row.change ? "ready" : "unchanged";
    return [row];
  });
  const matching = new Map<string, SpreadsheetRow[]>();
  rows.filter((row) => row.item && row.status !== "deferred" && row.status !== "saved").forEach((row) => matching.set(row.item!.id, [...(matching.get(row.item!.id) ?? []), row]));
  for (const duplicate of matching.values()) if (duplicate.length > 1) for (const row of duplicate) {
    row.issues.push({ field: "item_id", kind: "duplicate", message: `This item also appears on row ${duplicate.filter((other) => other !== row).map((other) => other.rowNumber).join(", ")}. Keep one row or leave the others for later.` }); row.status = "attention";
  }
  return rows;
}

export interface SpreadsheetBatch { request: CatalogueUpdateInput; rowKeys: string[] }
export interface SpreadsheetJob { batches: SpreadsheetBatch[]; completed: number; savedCount: number; total: number; unchanged?: number; deferred?: number; error?: "conflict" | "rejected" | "uncertain"; message?: string }
export function createSpreadsheetJob(rows: Pick<SpreadsheetRow, "key" | "status" | "change">[], id = () => crypto.randomUUID()): SpreadsheetJob {
  const ready = rows.filter((row) => row.status === "ready" && row.change);
  if (!ready.length) throw new Error("There are no changes to save.");
  if (rows.some((row) => row.status === "attention")) throw new Error("Resolve the rows needing attention or choose to leave them for later.");
  const batches: SpreadsheetBatch[] = [];
  for (let index = 0; index < ready.length; index += MAX_CATALOGUE_UPDATE) {
    const slice = ready.slice(index, index + MAX_CATALOGUE_UPDATE);
    batches.push({ request: { operationID: id(), changes: slice.map((row) => row.change!) }, rowKeys: slice.map((row) => row.key) });
  }
  return { batches, completed: 0, savedCount: 0, total: ready.length };
}
